// Dansk substantiv-bøjning via COR (ordregister.dk) — artikel, bestemt form, cache.

const COR_LEMMA_URL = 'https://ordregister.dk/lemma/COR/json/';
const CACHE_STORAGE_KEY = 'blindskrift:cor-morph:v1';

export type NounGender = 'fk' | 'itk';
export type IndefiniteArticle = 'en' | 'et';

export interface CorNounMorph {
  indefiniteArticle: IndefiniteArticle;
  definiteSingular: string;
  gender: NounGender;
}

interface CorLemmaEntry {
  lemma?: string;
  ordklasse?: string;
  'grammatisk function'?: string;
  fuldform?: string;
}

interface CorLemmaResponse {
  status?: string;
  svar?: CorLemmaEntry[];
}

const memoryCache = new Map<string, CorNounMorph>();

function readStorageCache(): Record<string, CorNounMorph> {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CorNounMorph>;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeStorageCache(map: Record<string, CorNounMorph>): void {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

function cacheKey(lemma: string): string {
  return lemma.trim().toLowerCase();
}

function genderFromGramFunction(gf: string): NounGender {
  return gf.includes('.itk.') ? 'itk' : 'fk';
}

function isSingularIndefinite(gf: string): boolean {
  return /\.sg\.ubest\.?$/.test(gf) && !gf.includes('.gen');
}

function isSingularDefinite(gf: string): boolean {
  return /\.sg\.best$/.test(gf) && !gf.includes('.gen');
}

function pickNounMorphFromCorEntries(
  entries: CorLemmaEntry[],
): CorNounMorph | undefined {
  let gender: NounGender | undefined;

  for (const row of entries) {
    if (row.ordklasse !== 'sb') continue;
    const gf = row['grammatisk function'] ?? '';
    if (!isSingularIndefinite(gf)) continue;
    gender = genderFromGramFunction(gf);
    break;
  }

  if (!gender) return undefined;

  const indefiniteArticle: IndefiniteArticle = gender === 'itk' ? 'et' : 'en';

  for (const row of entries) {
    if (row.ordklasse !== 'sb') continue;
    const gf = row['grammatisk function'] ?? '';
    if (!gf.includes(`.${gender}.`) || !isSingularDefinite(gf)) continue;
    const form = row.fuldform?.trim();
    if (form) {
      return { indefiniteArticle, definiteSingular: form, gender };
    }
  }

  return undefined;
}

/** Enkel bestemt form når COR ikke har ordet (offline / kreative ord). */
export function definiteFormDaFallback(word: string): string {
  const w = word.trim().toLowerCase();
  if (!w) return w;
  if (/(en|et)$/.test(w) && w.length > 3) return w;
  if (w.endsWith('e')) return w + 'n';
  return w + 'en';
}

function morphFallback(lemma: string): CorNounMorph {
  return {
    indefiniteArticle: 'en',
    definiteSingular: definiteFormDaFallback(lemma),
    gender: 'fk',
  };
}

async function fetchCorLemmaForms(lemma: string): Promise<CorLemmaEntry[]> {
  const key = cacheKey(lemma);
  const url = `${COR_LEMMA_URL}${encodeURIComponent(key)}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = (await res.json()) as CorLemmaResponse;
  if (data.status !== 'ok' || !Array.isArray(data.svar)) return [];
  return data.svar;
}

function storeMorph(lemma: string, morph: CorNounMorph): void {
  const key = cacheKey(lemma);
  memoryCache.set(key, morph);
  const stored = readStorageCache();
  stored[key] = morph;
  writeStorageCache(stored);
}

/** Synkron opslag — kun cache + hukommelse. */
export function getCachedNounMorph(lemma: string): CorNounMorph | undefined {
  const key = cacheKey(lemma);
  if (memoryCache.has(key)) return memoryCache.get(key);
  const stored = readStorageCache()[key];
  if (stored) {
    memoryCache.set(key, stored);
    return stored;
  }
  return undefined;
}

/**
 * Slår artikel (en/et) og bestemt ental op i COR.
 * Ved homografer vælges første substantiv-ubestemt-træff.
 */
export async function lookupNounMorphDa(lemma: string): Promise<CorNounMorph> {
  const raw = lemma.trim();
  if (!raw) return morphFallback(raw);

  const cached = getCachedNounMorph(raw);
  if (cached) return cached;

  try {
    const entries = await fetchCorLemmaForms(raw);
    const fromCor = pickNounMorphFromCorEntries(entries);
    const morph = fromCor ?? morphFallback(raw);
    storeMorph(raw, morph);
    return morph;
  } catch {
    const morph = morphFallback(raw);
    storeMorph(raw, morph);
    return morph;
  }
}

/** Synkron: cache → heuristik. */
export function resolveNounMorphDa(lemma: string): CorNounMorph {
  return getCachedNounMorph(lemma) ?? morphFallback(lemma);
}

/** @deprecated Brug lookupNounMorphDa — returnerer kun bestemt form. */
export async function lookupDefiniteFormDa(lemma: string): Promise<string> {
  const morph = await lookupNounMorphDa(lemma);
  return morph.definiteSingular;
}

/** @deprecated Brug resolveNounMorphDa. */
export function getCachedDefiniteForm(lemma: string): string | undefined {
  return getCachedNounMorph(lemma)?.definiteSingular;
}

/** @deprecated Brug resolveNounMorphDa. */
export function resolveDefiniteFormDa(lemma: string): string {
  return resolveNounMorphDa(lemma).definiteSingular;
}
